
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

export const meta = {
  title: 'Deploying ASP.NET Core on a Raspberry Pi using Docker',
  date: '2022-05-10',
  excerpt: 'Utilizing my Raspberry Pi 3B to run one of the projects I made a while ago with the help of docker'
};

const layoutProps = {
  meta
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <h2>{`Motivation`}</h2>
    <p>{`I had a `}<a parentName="p" {...{
        "href": "https://www.raspberrypi.org/"
      }}>{`Raspberry Pi`}</a>{` 3B and figured it would be a good machine to run my personal projects. I don't need a lot of computing power so it fits my needs nicely. I also had a project that I made a while ago called `}<a parentName="p" {...{
        "href": "http://github.com/BenasB/PUTS"
      }}>{`PUTS`}</a>{` where the main part is an ASP.NET Core MVC project called PUTSWeb (I'll be using them interchangeably). Combining these two would certainly be an interesting learning experience.`}</p>
    <h2>{`Goal`}</h2>
    <p>{`Use CI/CD to automatically deploy and run the PUTS project on my Raspberry Pi.`}</p>
    <h2>{`Implementation`}</h2>
    <p>{`PUTS depends on a MySQL database to store information about users, problems and solutions meaning that I'd need to also deploy a database alongside PUTS. For that purpose, I chose to use the last missing part of this article – `}<a parentName="p" {...{
        "href": "https://www.docker.com/"
      }}>{`Docker`}</a>{`. There's already an image for MySQL meaning that I could deploy the database using Docker and deploy PUTS regularly using dotnet, however, I decided to containerize PUTS and use `}<a parentName="p" {...{
        "href": "https://docs.docker.com/compose/"
      }}>{`Docker Compose`}</a>{` as this is probably the most convenient approach for me.`}</p>
    <p>{`Firstly I had to containerize PUTS. I decided to do all of the actual .NET building outside the Dockerfile in the CI/CD runner so that the image size is kept as small as possible. Because of this, the `}<a parentName="p" {...{
        "href": "https://github.com/BenasB/PUTS/blob/master/PUTSWeb/Dockerfile"
      }}>{`Dockerfile`}</a>{` came out very small and simple:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`FROM mcr.microsoft.com/dotnet/aspnet:2.1-bionic-arm32v7
RUN apt-get update && apt -y install g++
WORKDIR /app
COPY published/ .
ENTRYPOINT ["dotnet", "PUTSWeb.dll"]
`}</code></pre>
    <ul>
      <li parentName="ul">{`I need `}<inlineCode parentName="li">{`g++`}</inlineCode>{` in the container because PUTS uses `}<inlineCode parentName="li">{`g++`}</inlineCode>{` to compile the user submitted programs.`}</li>
      <li parentName="ul">{`I'm using the `}<inlineCode parentName="li">{`-bionic-arm32v7`}</inlineCode>{` version of the dotnet image because I'm making an image for a Raspberry Pi 3B and its architecture is `}<inlineCode parentName="li">{`arm32v7`}</inlineCode>{` (keep in mind that newer Raspberry Pis have a different one).`}</li>
    </ul>
    <p>{`In theory, I could now build a docker image for PUTS (in practice I couldn't directly build an image for a different architecture) that I'll eventually push to `}<a parentName="p" {...{
        "href": "https://hub.docker.com/u/benasbudrys"
      }}>{`Docker Hub`}</a>{`. The next implementation step was to create a `}<inlineCode parentName="p">{`docker-compose`}</inlineCode>{` file which I refer to as `}<a parentName="p" {...{
        "href": "https://github.com/BenasB/PUTS/blob/master/Infrastructure/rpi-stack.yml"
      }}>{`rpi-stack.yml`}</a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yml"
      }}>{`version: '3.1'

services:
  puts:
    image: benasbudrys/putsweb
    restart: always
    ports:
      - 8000:5001
    volumes:
      - keys:/app/Keys
      - ~/.aspnet/https:/https:ro
    depends_on:
      - db
    environment:
      ConnectionStrings__ProblemDatabase: $ConnectionStrings__ProblemDatabase
      ASPNETCORE_Kestrel__Certificates__Default__Password: $ASPNETCORE_Kestrel__Certificates__Default__Password
      ASPNETCORE_Kestrel__Certificates__Default__Path: $ASPNETCORE_Kestrel__Certificates__Default__Path
      ASPNETCORE_URLS: https://+:5001
  db:
    image: jsurf/rpi-mariadb
    restart: always
    ports:
      - 8001:3306
    volumes:
      - db-data:/var/lib/mysql
    environment:
      MYSQL_ROOT_PASSWORD: $MYSQL_ROOT_PASSWORD
  adminer:
    image: adminer
    restart: always
    ports:
      - 8002:8080

volumes:
  db-data:
  keys:
`}</code></pre>
    <p>{`There are a couple of things worth noting about this compose file:`}</p>
    <ul>
      <li parentName="ul">{`The service `}<inlineCode parentName="li">{`adminer`}</inlineCode>{` is completely optional, I just wanted something to conveniently check my database.`}</li>
      <li parentName="ul">{`I'm using `}<inlineCode parentName="li">{`jsurf/rpi-mariadb`}</inlineCode>{` instead of a regular `}<inlineCode parentName="li">{`mysql`}</inlineCode>{` image for the database because this one is suited for a Raspberry Pi.`}</li>
      <li parentName="ul">{`Ports `}<em parentName="li">{`8000`}</em>{`, `}<em parentName="li">{`8001`}</em>{` and `}<em parentName="li">{`8002`}</em>{` are completely arbitrary`}</li>
      <li parentName="ul">{`There are several environment variables that will be passed in the compose file via GitHub environment variables.`}</li>
      <li parentName="ul">{`There are project specific volumes for ASP.NET Core Identity and `}<a parentName="li" {...{
          "href": "https://docs.microsoft.com/en-us/aspnet/core/security/docker-compose-https?view=aspnetcore-6.0"
        }}>{`HTTPS certificate`}</a>{`.`}</li>
    </ul>
    <p>{`I now had a Dockerfile and a compose/stack file for my project, all I needed now is to somehow get everything onto the Raspberry Pi and compose up the stack. It's finally time for the pipeline which consists of these logical steps:`}</p>
    <ol>
      <li parentName="ol">{`Build and publish Docker image`}
        <ol parentName="li">
          <li parentName="ol">{`Setup dotnet`}</li>
          <li parentName="ol">{`Build dotnet`}</li>
          <li parentName="ol">{`Publish dotnet`}</li>
          <li parentName="ol">{`Build Docker image`}</li>
          <li parentName="ol">{`Push Docker image to Docker Hub`}</li>
        </ol>
      </li>
      <li parentName="ol">{`Deploy on Raspberry Pi`}
        <ol parentName="li">
          <li parentName="ol">{`Copy stack file over to the Pi`}</li>
          <li parentName="ol">{`Compose pull`}</li>
          <li parentName="ol">{`Compose up`}</li>
        </ol>
      </li>
    </ol>
    <p>{`I store all of my personal projects on `}<a parentName="p" {...{
        "href": "https://github.com/BenasB"
      }}>{`my GitHub`}</a>{` so I'll be using Github Actions to create a CI/CD pipeline. The `}<a parentName="p" {...{
        "href": "https://github.com/BenasB/PUTS/blob/master/.github/workflows/putsweb.yml"
      }}>{`pipeline`}</a>{` I'm currently using came out like this (don't be discouraged by quite a few steps):`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-yml"
      }}>{`name: PUTSWeb

on:
  push:
    branches: [master]
  workflow_dispatch:

jobs:
  docker-image:
    name: Build and publish docker image
    runs-on: ubuntu-latest
    defaults:
      run:
        working-directory: ./PUTSWeb
    steps:
      - name: Checkout 🛎️
        uses: actions/checkout@v3

      - name: Setup .NET 🌐
        uses: actions/setup-dotnet@v2
        with:
          dotnet-version: 2.1

      - name: Build dotnet 🔧
        run: dotnet build

      - name: Publish 📦
        run: dotnet publish -c Release -o published

      - name: Set up QEMU 🔨
        uses: docker/setup-qemu-action@v1

      - name: Set up Docker Buildx 🪛
        id: buildx
        uses: docker/setup-buildx-action@v1

      - name: Login to Docker Hub 🐋
        uses: docker/login-action@v1
        with:
          username: \${{ secrets.DOCKER_HUB_USERNAME }}
          password: \${{ secrets.DOCKER_HUB_ACCESS_TOKEN }}

      - name: Build and push ⬆️
        uses: docker/build-push-action@v2
        with:
          context: ./PUTSWeb/
          platforms: linux/arm/v7
          push: true
          tags: \${{ secrets.DOCKER_HUB_USERNAME }}/putsweb:latest

  deploy-rpi:
    name: Deploy stack to raspberry pi
    needs: docker-image
    runs-on: ubuntu-latest
    environment:
      name: pi
      url: http://benaspi.ddns.net:8000
    steps:
      - name: Checkout 🛎️
        uses: actions/checkout@v3

      - name: Copy stack file 📁
        uses: appleboy/scp-action@master
        with:
          host: \${{ secrets.RPI_HOST }}
          username: \${{ secrets.RPI_USERNAME }}
          password: \${{ secrets.RPI_PASSWORD }}
          source: 'Infrastructure/rpi-stack.yml'
          target: '~/stacks/putsweb/'
          strip_components: 1

      - name: Compose pull ⬇️
        uses: appleboy/ssh-action@master
        with:
          host: \${{ secrets.RPI_HOST }}
          username: \${{ secrets.RPI_USERNAME }}
          password: \${{ secrets.RPI_PASSWORD }}
          script: docker-compose -f ~/stacks/putsweb/rpi-stack.yml pull

      - name: Compose up 🚀
        uses: appleboy/ssh-action@master
        with:
          host: \${{ secrets.RPI_HOST }}
          username: \${{ secrets.RPI_USERNAME }}
          password: \${{ secrets.RPI_PASSWORD }}
          script: |
            export ConnectionStrings__ProblemDatabase="\${{ secrets.CONNECTIONSTRINGS__PROBLEMDATABASE }}"
            export MYSQL_ROOT_PASSWORD="\${{ secrets.MYSQL_ROOT_PASSWORD }}"
            export ASPNETCORE_Kestrel__Certificates__Default__Password="\${{ secrets.ASPNETCORE_KESTREL__CERTIFICATES__DEFAULT__PASSWORD }}"
            export ASPNETCORE_Kestrel__Certificates__Default__Path="\${{ secrets.ASPNETCORE_KESTREL__CERTIFICATES__DEFAULT__PATH }}"
            docker-compose -f ~/stacks/putsweb/rpi-stack.yml up -d

      - name: Check running containers 🔍
        uses: appleboy/ssh-action@master
        with:
          host: \${{ secrets.RPI_HOST }}
          username: \${{ secrets.RPI_USERNAME }}
          password: \${{ secrets.RPI_PASSWORD }}
          script: docker ps -a
`}</code></pre>
    <p>{`Some comments about the pipeline:`}</p>
    <ul>
      <li parentName="ul">{`It makes use of other pre-built GitHub Actions (as it should) to for example copy over the stack file to the Raspberry Pi.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`QEMU`}</inlineCode>{` and `}<inlineCode parentName="li">{`Buildx`}</inlineCode>{` are needed to build the image since this runner is using ubuntu and I'm building an image for the `}<inlineCode parentName="li">{`arm32v7`}</inlineCode>{` architecture.`}</li>
      <li parentName="ul">{`All sensitive data is handled by GitHub repository secrets and environment secrets.`}</li>
    </ul>
    <p>{`With everything in place, I was able to achieve what I wanted – upon PUTS code changes, a pipeline starts automatically, builds a Docker image for the PUTS project, pushes it to the Docker Hub and deploys the stack on the Raspberry Pi. What I like is that most of the things related to this project live in the repository together with PUTS code – I can see and change the stack file, update the pipeline etc.`}</p>
    <p><img parentName="p" {...{
        "src": "raster/puts-pipeline.png",
        "alt": "PUTS pipeline in GitHub"
      }}></img></p>
    <h3>{`Raspberry Pi as a runner`}</h3>
    <p>{`I also experimented a bit with the idea of having the Raspberry Pi act as a `}<a parentName="p" {...{
        "href": "https://docs.github.com/en/actions/hosting-your-own-runners/about-self-hosted-runners"
      }}>{`GitHub Actions runner`}</a>{`. However, I scrapped this idea because I didn't want to put additional load on the Pi itself and the runners provided by GitHub have more resources/are faster.`}</p>
    <p><em parentName="p">{`Just a heads up that the actual progress wasn't as straightforward as I've described in this article and I've discovered a lot of things with trial and error`}</em></p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;